"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CircleCheck } from "lucide-react";
const processSteps = [{
  number: 1,
  title: "Request Your Cash Offer",
  description: "Fill out our simple online form with your property details. No obligation, no pressure—just honest information to get started.",
  illustration: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1510319/img/request_cash_offer_illustration.png"
}, {
  number: 2,
  title: "Receive Your All-Cash Offer",
  description: "Our team evaluates your property and presents a transparent, competitive cash offer within 24 hours.",
  illustration: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1510319/img/receive_cash_offer_illustration.png"
}, {
  number: 3,
  title: "We Handle All the Paperwork",
  description: "Accept our offer and relax. CashHome covers title fees, closing fees, and transfer fees—no hidden costs, no surprises.",
  illustration: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1510319/img/handle_paperwork_illustration.png"
}, {
  number: 4,
  title: "Close Fast and Get Paid",
  description: "Choose your closing date and receive your cash payment. We move at your pace, ensuring a smooth, stress-free transaction.",
  illustration: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1510319/img/close_fast_get_paid_illustration.png"
}];
function ProcessStep({
  step,
  index
}: {
  step: typeof processSteps[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.15
  });
  return <div ref={ref} data-index={index} className={`transition-all duration-[var(--duration-medium)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
    transitionDelay: `${index * 100}ms`
  }}>
      <Card className="group hover:-translate-y-1.5 transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] hover:shadow-medium border border-border">
        <CardContent className="flex flex-col md:flex-row gap-6 items-start py-8">
          <div className="flex-shrink-0 flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 text-primary">
            <span className="text-3xl font-display font-bold">{step.number}</span>
          </div>
          
          <div className="flex-1 space-y-3">
            <CardHeader className="p-0">
              <CardTitle className="text-2xl font-display text-foreground">{step.title}</CardTitle>
            </CardHeader>
            <p className="text-muted-foreground leading-relaxed">{step.description}</p>
          </div>

          <div className="flex-shrink-0 w-full md:w-48 h-48 rounded-lg overflow-hidden">
            <img src={step.illustration} alt={step.title} className="w-full h-full object-cover transition-opacity duration-[var(--duration-medium)] group-hover:opacity-75" />
          </div>
        </CardContent>
      </Card>
    </div>;
}
export default function OurProcess() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  return <section id="our-process" className="py-20 px-4 md:px-8 bg-background">
      <div className="max-w-6xl mx-auto">
        <div ref={headerRef} className={`text-center mb-16 transition-all duration-[var(--duration-medium)] ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <h2 className="text-4xl md:text-5xl font-display font-bold text-foreground mb-4">
            Our Process
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            From initial contact to cash in hand, we've streamlined every step to make selling your home effortless.
          </p>
        </div>

        <div className="space-y-6">
          {processSteps.map((step, index) => <ProcessStep key={step.number} step={step} index={index} />)}
        </div>

        <div className="mt-12 text-center">
          <CircleCheck className="inline-block w-12 h-12 text-primary mb-4" />
          <p className="text-muted-foreground text-lg">
            Ready to experience a hassle-free home sale? Let's get started today.
          </p>
        </div>
      </div>
    </section>;
}